import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  linkedSignal,
  model,
  signal,
} from '@angular/core';
import type { FormValueControl } from '@angular/forms/signals';
import { UiFieldControl } from '../../../basics/ui-field-control';
import { InputFieldComponent } from '../../atoms/input-field/input-field.component';
import { ValidationErrorUIComponent } from '../../atoms/validation-error/validation-error.component';
import {
  cellInputDigit,
  DigitCell,
  DigitCellsState,
  initialActiveIndex,
  joinCells,
  reconcileCells,
  reduceKeyDown,
  reducePaste,
  setCell,
} from './_core/digit-cells';

const DEFAULT_DIGITS_COUNT = 6;

@Component({
  selector: 'fr8bot-ui-digits-code-field',
  imports: [InputFieldComponent, ValidationErrorUIComponent],
  templateUrl: './digits-code-field.component.html',
  styleUrl: './digits-code-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DigitsCodeFieldComponent extends UiFieldControl implements FormValueControl<string> {
  readonly value = model('');
  readonly digitsCount = input(DEFAULT_DIGITS_COUNT);

  protected readonly cells = linkedSignal<{ value: string; count: number }, DigitCell[]>({
    source: () => ({ value: this.value() ?? '', count: this.digitsCount() }),
    computation: (source, previous) => reconcileCells(previous?.value, source.value, source.count),
  });

  protected readonly activeIndex = linkedSignal<DigitCell[], number>({
    source: this.cells,
    computation: (cells, previous) => (previous ? previous.value : initialActiveIndex(cells)),
  });

  // Typing moves focus from cell to cell, so the error waits until focus leaves the cells.
  private readonly isGroupLeft = signal(false);
  protected readonly showError = computed(() => this.isGroupLeft() && this.isErrorShown());

  protected onKeyDown(event: KeyboardEvent): void {
    if (event.metaKey || event.ctrlKey) {
      return;
    }

    event.preventDefault();

    this.apply(
      reduceKeyDown(
        { cells: this.cells(), activeIndex: this.activeIndex() },
        event.key,
        this.digitsCount(),
      ),
    );
  }

  protected onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const text = event.clipboardData?.getData('text');

    if (text !== undefined) {
      this.apply(reducePaste(text, this.digitsCount()));
    }
  }

  // Keyboards that bypass `keydown` (Android IME) still reach the cell as an input event.
  protected onCellInput(index: number, value: unknown): void {
    this.writeCells(setCell(this.cells(), index, cellInputDigit(value)));
  }

  protected onFocus(index: number): void {
    this.activeIndex.set(index);
    this.isGroupLeft.set(false);
  }

  protected onFocusOut(event: FocusEvent): void {
    const group = event.currentTarget as HTMLElement;

    this.isGroupLeft.set(!group.contains(event.relatedTarget as Node | null));
  }

  private apply(next: DigitCellsState): void {
    if (next.cells !== this.cells()) {
      this.writeCells(next.cells);
    }
    this.activeIndex.set(next.activeIndex);
  }

  private writeCells(cells: DigitCell[]): void {
    this.cells.set(cells);
    this.value.set(joinCells(cells));
  }
}
